/**
 * Na-inspectie List Page
 *
 * Shows all items awaiting na-inspectie (post-inspection quality control).
 * Items are filtered by: Status__c IN ('Drogen', 'In Nabehandeling', 'Randen Plakken', 'In Reparatie')
 * AND Product__r.Wasserij_item__c = true
 *
 * Features:
 * - Search by item number (WI-xxxxx), work order, or product name
 * - Shows all Extra's as badges (Priority items like Spoed first)
 * - Click item to go to quality control form
 * - Caches items in IndexedDB for offline support
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { WbButton, WbChip, WbFlag, WbListRow, WbSearchField, WbSectionKop, WbState } from '../components/wb';
import { useHeader } from '../stores';
import { salesforceClient } from '../lib/salesforce';
import { PRIORITY_PRODUCT_CODES } from '../lib/constants';
import {
  getCachedNaInspectieItems,
  cacheNaInspectieItems,
  type CachedNaInspectieItem,
} from '../lib/db/database';
import type { NaInspectieItem } from '../lib/salesforce';
import { matchtZoek } from '../lib/utils/zoek';
import { WachtrijMislukt } from '../components/WachtrijMislukt';

// Polling interval for real-time updates (60 seconds)
const POLLING_INTERVAL_MS = 60000;

export function NaInspectieListPage() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [items, setItems] = useState<NaInspectieItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch items from API or cache
  const fetchItems = useCallback(async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      // Try API first
      const response = await salesforceClient.getNaInspectieItems();
      console.log('[NaInspectieListPage] API response:', response);

      if (response.success && response.data) {
        const apiItems = response.data.items || [];
        setItems(apiItems);

        // Cache items for offline use
        const itemsToCache: CachedNaInspectieItem[] = apiItems.map(item => ({
          itemId: item.itemId,
          itemName: item.itemName,
          productName: item.productName,
          werkOrderId: item.werkOrderId,
          werkOrderName: item.werkOrderName,
          status: item.status,
          companyCamProjectId: item.companyCamProjectId,
          fotosUrl: item.fotosUrl,
          lengthCm: item.lengthCm,
          widthCm: item.widthCm,
          diameterCm: item.diameterCm,
          surfaceM2: item.surfaceM2,
          vorm: item.vorm,
          voorinspectie: item.voorinspectie,
          voorinspectieOpmerkingen: item.voorinspectieOpmerkingen,
          wasrapport: item.wasrapport,
          secondaryItems: item.secondaryItems,
          isKlacht: item.isKlacht,
          klachtReden: item.klachtReden,
          reparatieNummer: item.reparatieNummer,
          reparatieStatus: item.reparatieStatus,
          reparatieOmschrijving: item.reparatieOmschrijving,
          _cachedAt: new Date(),
        }));
        await cacheNaInspectieItems(itemsToCache);
      } else {
        // Try cache on API failure
        console.warn('[NaInspectieListPage] API failed, trying cache');
        const cachedItems = await getCachedNaInspectieItems();
        if (cachedItems.length > 0) {
          const mappedItems: NaInspectieItem[] = cachedItems.map(item => ({
            itemId: item.itemId,
            itemName: item.itemName,
            productName: item.productName,
            werkOrderId: item.werkOrderId,
            werkOrderName: item.werkOrderName,
            status: item.status,
            companyCamProjectId: item.companyCamProjectId,
            fotosUrl: item.fotosUrl,
            lengthCm: item.lengthCm,
            widthCm: item.widthCm,
            diameterCm: item.diameterCm,
            surfaceM2: item.surfaceM2,
            vorm: item.vorm,
            voorinspectie: item.voorinspectie,
            voorinspectieOpmerkingen: item.voorinspectieOpmerkingen,
            wasrapport: item.wasrapport,
            secondaryItems: item.secondaryItems,
            isKlacht: item.isKlacht,
            klachtReden: item.klachtReden,
            reparatieNummer: item.reparatieNummer,
            reparatieStatus: item.reparatieStatus,
            reparatieOmschrijving: item.reparatieOmschrijving,
          }));
          setItems(mappedItems);
        } else {
          setError(response.message || 'Kon items niet ophalen');
          setItems([]);
        }
      }
    } catch (err) {
      console.error('[NaInspectieListPage] Error fetching items:', err);

      // Try cache on network error
      try {
        const cachedItems = await getCachedNaInspectieItems();
        if (cachedItems.length > 0) {
          const mappedItems: NaInspectieItem[] = cachedItems.map(item => ({
            itemId: item.itemId,
            itemName: item.itemName,
            productName: item.productName,
            werkOrderId: item.werkOrderId,
            werkOrderName: item.werkOrderName,
            status: item.status,
            companyCamProjectId: item.companyCamProjectId,
            fotosUrl: item.fotosUrl,
            lengthCm: item.lengthCm,
            widthCm: item.widthCm,
            diameterCm: item.diameterCm,
            surfaceM2: item.surfaceM2,
            vorm: item.vorm,
            voorinspectie: item.voorinspectie,
            voorinspectieOpmerkingen: item.voorinspectieOpmerkingen,
            wasrapport: item.wasrapport,
            secondaryItems: item.secondaryItems,
            isKlacht: item.isKlacht,
            klachtReden: item.klachtReden,
          }));
          setItems(mappedItems);
          setError('Offline modus - gegevens kunnen verouderd zijn');
        } else {
          setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden');
          setItems([]);
        }
      } catch {
        setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden');
        setItems([]);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Fetch items on mount
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Polling for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchItems(true);
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchItems]);

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return items;
    }

    return items.filter(item => matchtZoek(searchQuery, item.itemName, item.werkOrderName, item.productName));
  }, [items, searchQuery]);

  // Te lang in deze status (Drogen > 6 dagen): meestal fysiek al verder maar
  // niet gescand. Zolang staat de hele order stil en krijgt de klant geen
  // aflevermail (#144/#159). Bovenaan, langst eerst.
  const teLang = (item: NaInspectieItem) =>
    item.stilDrempelDagen !== undefined && item.stilDagen != null && item.stilDagen >= item.stilDrempelDagen;
  const vastgelopen = useMemo(
    () => filteredItems.filter(teLang).sort((a, b) => (b.stilDagen ?? 0) - (a.stilDagen ?? 0)),
    [filteredItems]
  );
  const gewoon = useMemo(() => filteredItems.filter((i) => !teLang(i)), [filteredItems]);

  // Handle refresh
  const handleRefresh = async () => {
    await fetchItems(true);
  };

  const maatVan = (item: NaInspectieItem) => {
    if (item.lengthCm && item.widthCm) return `${item.lengthCm} × ${item.widthCm}`;
    if (item.diameterCm) return `Ø ${item.diameterCm}`;
    if (item.surfaceM2) return `${item.surfaceM2.toFixed(1)} m²`;
    return null;
  };

  // Extra's: Spoed en Premium eerst (PRIORITY_PRODUCT_CODES), daarna de rest.
  const sortSecondaryItems = (secondaryItems: NaInspectieItem['secondaryItems']) =>
    [...secondaryItems].sort((a, b) => {
      const aIsPriority = PRIORITY_PRODUCT_CODES.includes(a.productCode as typeof PRIORITY_PRODUCT_CODES[number]);
      const bIsPriority = PRIORITY_PRODUCT_CODES.includes(b.productCode as typeof PRIORITY_PRODUCT_CODES[number]);
      if (aIsPriority && !bIsPriority) return -1;
      if (!aIsPriority && bIsPriority) return 1;
      return 0;
    });

  const rij = (item: NaInspectieItem) => {
    const extras = sortSecondaryItems(item.secondaryItems);
    const maat = maatVan(item);
    return (
      <WbListRow
        key={item.itemId}
        onClick={() => navigate(`/na-inspectie/${item.itemId}`)}
        hoofd={item.itemName}
        chips={
          <>
            {item.status !== 'Drogen' && <WbChip kind={item.status === 'In Reparatie' ? 'actie' : 'wacht'}>{item.status}</WbChip>}
            {teLang(item) && <WbChip kind="actie">{item.stilDagen} dagen op {item.status}</WbChip>}
            {teLang(item) && (item.broersVerder ?? 0) > 0 && <WbChip kind="wacht">rest van order ingepakt</WbChip>}
            {item.isKlacht && <WbChip kind="klacht">Klacht</WbChip>}
            {extras.map((extra) => (
              <WbChip
                key={extra.itemId}
                kind={extra.productCode === 'WAS-PRIM-VLOERKLEED-SPOED' ? 'actie' : extra.productCode === 'WAS-PACK-VLOERKLEED-PREMIUM' ? 'klaar' : 'neutraal'}
              >
                {extra.productName}
              </WbChip>
            ))}
          </>
        }
        sub={[maat, item.werkOrderName, item.productName].filter(Boolean).join(' · ')}
      />
    );
  };

  useHeader('Na-inspectie', `${items.length}`);

  return (
    <div className="space-y-4">
      <WbSearchField value={searchQuery} onChange={setSearchQuery} placeholder="Zoek WI-nummer, order of product" />
      <WachtrijMislukt soort="na-inspectie" />

      {isLoading && <WbState variant="laden" titel="Items laden…" />}
      {!isLoading && error && items.length === 0 && <WbState variant="fout" titel={error} onOpnieuw={() => fetchItems()} />}
      {!isLoading && error && items.length > 0 && <WbFlag kind="opmerking">{error}</WbFlag>}
      {!isLoading && !error && items.length === 0 && <WbState variant="leeg" titel="Alle na-inspecties gedaan." tekst="Er zijn geen kleden die kwaliteitscontrole nodig hebben." />}
      {!isLoading && !error && items.length > 0 && filteredItems.length === 0 && (
        <WbState variant="leeg" titel={`Geen resultaten voor "${searchQuery}" in deze lijst.`}>
          <Link to={`/zoek?q=${encodeURIComponent(searchQuery)}`} className="wb-focus inline-flex min-h-tap items-center font-semibold underline underline-offset-4 text-wb-inkt">
            Zoek "{searchQuery}" in alle kleden
          </Link>
        </WbState>
      )}

      {!isLoading && vastgelopen.length > 0 && (
        <section className="space-y-1.5">
          <WbSectionKop label="Staat te lang" teller={vastgelopen.length} />
          <WbFlag kind="opmerking">
            Deze kleden staan langer dan normaal in hun status. Meestal liggen ze al verder en is de scan vergeten. Zoek ze op en doe de na-inspectie: tot dan blijft de hele order staan en krijgt de klant geen aflevermail.
          </WbFlag>
          {vastgelopen.map((item) => rij(item))}
        </section>
      )}

      {!isLoading && gewoon.length > 0 && (
        <section className="space-y-1.5">
          <WbSectionKop
            label="Kwaliteitscontrole"
            teller={gewoon.length}
            actie={
              <WbButton variant="link" onClick={handleRefresh} disabled={isRefreshing}>
                {isRefreshing ? 'Verversen…' : 'Ververs'}
              </WbButton>
            }
          />
          {gewoon.map((item) => rij(item))}
        </section>
      )}
    </div>
  );
}
